import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/UI';
import { generateAssessmentPDF } from '../helpers/pdfGenerator';
import { kuesionerData } from '../helpers/kuesioner';
import RecommendationCard from '../components/RecommendationCard';

const KPSPStandard = ({ ageInMonths }) => {
    // Find the closest age group for KPSP questions
    const availableAges = Object.keys(kuesionerData.kpsp).map(Number);
    const closestAge = availableAges.reduce((prev, curr) => 
        (Math.abs(curr - ageInMonths) < Math.abs(prev - ageInMonths) ? curr : prev)
    );

    const questions = kuesionerData.kpsp[closestAge];

    return (
        <div className="mt-4 pt-5 border-t border-gray-200/50">
            <h6 className="font-extrabold text-xs uppercase tracking-widest text-indigo-800/60 mb-4 flex items-center">
                <span className="mr-2">📋</span> Standar KPSP Usia {closestAge} Bulan:
            </h6>
            <ul className="space-y-3">
                {questions.map((q, idx) => (
                    <li key={q.id} className="flex items-start space-x-3 bg-white/40 p-3 rounded-xl border border-white/60 shadow-sm">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-[10px] font-bold mt-0.5 shadow-sm">
                            {idx + 1}
                        </span>
                        <span className="text-sm text-gray-700 leading-relaxed font-medium">{q.q}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

// Fallback ResultCard for old data structures without recommendation logic
const FallbackResultCard = ({ title, data, children, index = 0 }) => {
    if (!data) return null;
    
    let statusColor = 'text-amber-700 bg-amber-50/80 border-amber-200';
    let icon = '⚠️';
    if (data.status === 'Sesuai' || data.status === 'Risiko Rendah' || data.status === 'Normal') {
        statusColor = 'text-emerald-700 bg-emerald-50/80 border-emerald-200';
        icon = '✨';
    }
    if (data.status.includes('Penyimpangan') || data.status.includes('Risiko') || data.status.includes('Kemungkinan')) {
        statusColor = 'text-rose-700 bg-rose-50/80 border-rose-200';
        icon = '🚨';
    }

    return (
        <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="mb-6 bg-white/70 backdrop-blur-2xl border border-white/80 rounded-[2rem] p-6 shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-xl transition-all duration-300"
        >
            <div className="flex items-center space-x-3 mb-5">
                <div className={`w-10 h-10 rounded-[1rem] ${statusColor.split(' ')[1]} border ${statusColor.split(' ')[2]} flex items-center justify-center text-lg shadow-sm`}>
                    {icon}
                </div>
                <h5 className="font-extrabold text-lg text-gray-800 tracking-tight">{title}</h5>
            </div>
            <div className={`p-5 rounded-[1.5rem] border backdrop-blur-md ${statusColor}`}>
                <div className="font-extrabold text-lg">{data.status}</div>
                {data.detail && <div className="text-sm mt-1 font-medium opacity-90">{data.detail}</div>}
                <div className={`w-8 h-1 rounded-full my-3 opacity-30 bg-current ${statusColor.split(' ')[0]}`}></div>
                <p className="text-sm opacity-95 font-medium leading-relaxed">{data.interpretasi}</p>
            </div>
            {children}
        </motion.div>
    );
};

export default function ResultScreen({ child, assessment, navigate }) {
    
    // Helper to determine if we should use RecommendationCard or Fallback
    const renderCard = (title, data, extraChildren = null, index = 0) => {
        if (!data) return null;
        if (data.rekomendasi || data.tindakLanjut || data.followUp || data.urgency) {
            return (
                <div key={title}>
                    <RecommendationCard title={title} data={data} index={index} />
                    {extraChildren && (
                        <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: (index * 0.1) + 0.2 }}
                            className="bg-white/50 backdrop-blur-xl rounded-[2rem] p-6 mt--2 mb-6 border border-white shadow-sm"
                        >
                            {extraChildren}
                        </motion.div>
                    )}
                </div>
            );
        }
        return <FallbackResultCard key={title} title={title} data={data} index={index}>{extraChildren}</FallbackResultCard>;
    };

    // Filter out critical results to show them at the top
    const allResults = [
        { title: "Pertumbuhan Fisik", data: assessment.pertumbuhan },
        { title: "Perkembangan (KPSP)", data: assessment.perkembangan, extra: assessment.perkembangan ? <KPSPStandard ageInMonths={assessment.ageInMonths} /> : null },
        { title: "Mental Emosional (KMME)", data: assessment.mental },
        { title: "Skrining GPPH", data: assessment.gpph },
        { title: "Tes Daya Dengar (TDD)", data: assessment.dayaDengar },
        { title: "Pemeriksaan Mata (Pupil Putih)", data: assessment.pemeriksaanMata?.pupilPutih },
        { title: "Pemeriksaan Mata (Daya Lihat)", data: assessment.pemeriksaanMata?.dayaLihat },
        { title: "Skrining Autisme (CHAT)", data: assessment.chat },
    ].filter(item => item.data);

    const criticalResults = allResults.filter(item => (item.data.urgency === 'high' || item.data.tingkatUrgency === 'high'));
    const normalResults = allResults.filter(item => !(item.data.urgency === 'high' || item.data.tingkatUrgency === 'high'));

    return (
        <div className="min-h-screen bg-[#F8FAFC] font-sans selection:bg-indigo-200 pb-28 pt-8 px-4 sm:px-6 relative overflow-hidden">
            {/* Animated Background Mesh - Modern & Subtle */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] right-[-10%] w-[60vw] h-[60vw] bg-indigo-300 rounded-full mix-blend-multiply filter blur-[120px] opacity-20 animate-blob"></div>
                <div className="absolute bottom-[-10%] left-[-10%] w-[70vw] h-[70vw] bg-purple-300 rounded-full mix-blend-multiply filter blur-[120px] opacity-20 animate-blob animation-delay-2000"></div>
                <div className="absolute top-[40%] left-[20%] w-[50vw] h-[50vw] bg-sky-300 rounded-full mix-blend-multiply filter blur-[120px] opacity-20 animate-blob animation-delay-4000"></div>
                <div className="absolute inset-0 bg-white/40 backdrop-blur-[20px]"></div>
            </div>

            <div className="relative z-10 max-w-lg mx-auto">
                {/* Header Card */}
                <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white/60 backdrop-blur-2xl rounded-[2.5rem] p-8 border border-white shadow-[0_8px_40px_rgba(0,0,0,0.06)] mb-10 text-center relative overflow-hidden"
                >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-indigo-100 to-transparent rounded-bl-full opacity-50"></div>
                    
                    <div className="w-20 h-20 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-[1.5rem] flex items-center justify-center text-4xl mx-auto mb-6 shadow-sm border border-white relative z-10">
                        📋
                    </div>
                    <h2 className="text-3xl font-extrabold text-gray-800 tracking-tight leading-tight relative z-10">{child.name}</h2>
                    {(child.schoolName || child.className) && (
                        <p className="text-xs text-gray-500 font-medium mt-1 relative z-10">
                            {child.schoolName || 'PAUD Tidak Diketahui'} {child.className ? `• Kelas ${child.className}` : ''}
                        </p>
                    )}
                    <p className="text-[13px] text-gray-500 mt-2 font-medium relative z-10">
                        Hasil Pemeriksaan pada <br/>
                        <span className="text-indigo-600 font-bold bg-indigo-50 px-2 py-1 rounded-md inline-block mt-1">
                            {assessment.createdAt.toDate().toLocaleString('id-ID', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                    </p>
                    
                    <button 
                        onClick={() => generateAssessmentPDF(child, assessment)}
                        className="mt-8 w-full bg-white border border-gray-200 text-gray-800 font-bold py-4 rounded-[1.25rem] hover:bg-gray-50 hover:border-gray-300 hover:shadow-lg transition-all duration-300 flex items-center justify-center space-x-3 group relative z-10"
                    >
                        <span className="text-xl group-hover:-translate-y-1 transition-transform">📥</span>
                        <span>Unduh Laporan Resmi (PDF)</span>
                    </button>
                </motion.div>

                {/* Critical Results Section */}
                {criticalResults.length > 0 && (
                    <div className="mb-10">
                        <motion.div 
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="flex items-center space-x-3 mb-5 px-3"
                        >
                            <span className="w-10 h-10 bg-rose-100 rounded-[1rem] flex items-center justify-center text-xl shadow-sm border border-rose-200">⚠️</span>
                            <h3 className="text-2xl font-extrabold text-rose-600 tracking-tight">Peringatan Kritis</h3>
                        </motion.div>
                        {criticalResults.map((item, index) => renderCard(item.title, item.data, item.extra, index))}
                    </div>
                )}

                {/* Normal Results Section */}
                {normalResults.length > 0 && (
                    <div className="mb-10">
                        <motion.div 
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="flex items-center space-x-3 mb-5 px-3"
                        >
                            <span className="w-10 h-10 bg-indigo-100 rounded-[1rem] flex items-center justify-center text-xl shadow-sm border border-indigo-200">📊</span>
                            <h3 className="text-2xl font-extrabold text-gray-800 tracking-tight">Rincian Hasil</h3>
                        </motion.div>
                        {normalResults.map((item, index) => renderCard(item.title, item.data, item.extra, criticalResults.length + index))}
                    </div>
                )}

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                >
                    <Button 
                        onClick={() => navigate('viewChild', child)}
                        className="w-full bg-gray-900 text-white hover:bg-gray-800 shadow-xl hover:shadow-2xl py-5 rounded-[1.5rem] mt-4 transition-all duration-300 font-extrabold text-[15px]"
                    >
                        Kembali ke Detail Anak
                    </Button>
                </motion.div>
            </div>

            <style>{`
                @keyframes blob {
                    0% { transform: translate(0px, 0px) scale(1); }
                    33% { transform: translate(40px, -60px) scale(1.2); }
                    66% { transform: translate(-30px, 30px) scale(0.8); }
                    100% { transform: translate(0px, 0px) scale(1); }
                }
                .animate-blob { animation: blob 20s infinite alternate ease-in-out; }
                .animation-delay-2000 { animation-delay: 2s; }
                .animation-delay-4000 { animation-delay: 4s; }
            `}</style>
        </div>
    );
}
